import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GUIDE_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
] as const;

const normalizeLanguage = (lang: string): string => lang.split('-')[0].toLowerCase();

interface GuideLanguageSelectorProps {
  className?: string;
  variant?: 'header' | 'inline';
}

const GuideLanguageSelector: React.FC<GuideLanguageSelectorProps> = ({
  className = '',
  variant = 'header',
}) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateLanguage = () => setCurrentLang(i18n.language);
    updateLanguage();
    i18n.on('languageChanged', updateLanguage);
    return () => {
      i18n.off('languageChanged', updateLanguage);
    };
  }, [i18n]);

  const changeLanguage = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      localStorage.setItem('i18nextLng', languageCode);
      setCurrentLang(languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalized = normalizeLanguage(currentLang);
  const current =
    GUIDE_LANGUAGES.find((lang) => lang.code === normalized) ?? GUIDE_LANGUAGES[0];

  const buttonClass =
    variant === 'header'
      ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/30 bg-dark-bg/60 text-amber-200/90 hover:bg-dark-bg hover:text-amber-100 text-sm transition-colors'
      : 'flex items-center gap-1 p-2 text-text-muted hover:text-text-primary transition-colors';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClass}
        title={t('language.selectLanguage')}
        aria-label={t('language.selectLanguage')}
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 shrink-0" />
        <span className="font-medium">{current.nativeName}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
      </button>

      {isOpen && (
        <div
          className={`absolute z-[110] w-44 rounded-lg border border-dark-border bg-dark-bg shadow-lg overflow-hidden ${
            variant === 'header' ? 'top-full right-0 mt-1' : 'bottom-full right-0 mb-1'
          }`}
        >
          <div className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wide border-b border-dark-border">
            {t('language.selectLanguage')}
          </div>
          {GUIDE_LANGUAGES.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => changeLanguage(language.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-dark-card transition-colors ${
                normalized === language.code
                  ? 'bg-dark-card text-accent-blue'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <span className="font-medium">{language.nativeName}</span>
              {normalized === language.code && (
                <span className="w-2 h-2 bg-accent-blue rounded-full" aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuideLanguageSelector;
